import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Preferences } from '@capacitor/preferences';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { EMPTY, catchError, filter, map, mergeMap, of, switchMap, take, tap } from 'rxjs';

import { CREATE_ENTITY_FLOW_ROUTE_PREFIX } from '@views/investor/investor.helpers';
import { loginStageUpdate } from '@views/login/login.actions';
import { LoginStageEnum } from '@views/login/login.types';

import { SnackBarService } from '@components/sq-snackbar/sq-snackbar.service';

import { BrazeEventEnum, BrazeEventPropertiesEnum, BrazeService } from '@core/utilities/braze.service';
import { LoggerService } from '@core/utilities/logger/logger.service';

import * as actions from './auth.actions';
import { AuthService } from './auth.service';
import { NativeLoginTypes, PreferenceStorage } from './auth.types';
import { NativeAuthService } from './native-auth.service';
import { StorageService } from './storage.service';

@Injectable()
export class Effects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private nativeAuthService = inject(NativeAuthService);
  private snackBarService = inject(SnackBarService);
  private loggerService = inject(LoggerService);
  private storageService = inject(StorageService);
  private brazeService = inject(BrazeService);
  private router = inject(Router);

  createUserSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authCreateUserSessionStart),
      switchMap(action => {
        return this.authService.createUserSession(action.payload).pipe(
          map(payload => {
            return actions.authCreateUserSessionSuccess({ payload });
          }),
          catchError(error => {
            let errorMessage = '';

            if (error.errors) {
              errorMessage = error.errors[0].errorMessage;
            } else {
              errorMessage = 'something went wrong in our systems, please try again later';
              if (error?.error?.errorMessage?.length > 0) {
                errorMessage = error.error.errorMessage;
              }
              this.loggerService.logEvent({ message: errorMessage, errorResponse: error });
              this.snackBarService.setError(errorMessage);
            }
            return of(actions.authCreateUserSessionFail());
          }),
        );
      }),
    ),
  );

  refreshShortTermTokenToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authRefreshShortTermTokenStart),
      switchMap(action => {
        const { longTermToken } = action;
        return this.authService.refreshShortTermToken({ longTermToken }).pipe(
          map(payload => {
            this.storageService.setShortToken(payload?.shortTermToken ?? '');
            return actions.authRefreshShortTermTokenSuccess();
          }),
        );
      }),
    ),
  );

  createUserSessionFromLongTermToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authCreateUserSessionFromLongTermTokenStart),
      switchMap(action => {
        const { longTermToken, userId, loginType } = action;
        if (!longTermToken || userId == null || Number.isNaN(userId)) {
          // Mirror the catchError recovery below: run the same side effects and dispatch the
          // same fail + login-stage actions so the reducer flips isLoading back to false instead
          // of stranding the user on a loading spinner.
          return of(...this.handleCreateUserSessionFromLongTermTokenFailure(loginType));
        }
        return this.authService.refreshShortTermToken({ longTermToken }).pipe(
          map(payload => {
            return actions.authCreateUserSessionSuccess({
              payload: {
                longTermToken,
                shortTermToken: payload?.shortTermToken,
                shortTermTokenExpiresInSeconds: payload?.shortTermTokenExpiresInSeconds,
                userId,
              },
            });
          }),
          catchError(() => of(...this.handleCreateUserSessionFromLongTermTokenFailure(loginType))),
        );
      }),
    ),
  );

  private handleCreateUserSessionFromLongTermTokenFailure(loginType: NativeLoginTypes | undefined) {
    if (loginType === NativeLoginTypes.Biometric || loginType === NativeLoginTypes.Pin) {
      // Reset the intro biometric flag so that they are asked to re enable it after they login via the form
      Preferences.remove({ key: PreferenceStorage.IntroBiometric });
      // Reset the intro pin flag so that they are asked to reset the pin after they login via the form
      Preferences.remove({ key: PreferenceStorage.IntroPin });
      this.snackBarService.setError('Your session has expired. Please login using your username and password.');
      // Clear the biometric so the user has to re-enable it
      this.nativeAuthService.deleteAllCredentials();
    }
    return [
      // Reset the login flow to be the login form
      loginStageUpdate({
        loginStage: LoginStageEnum.LoginForm,
      }),
      actions.authCreateUserSessionFail(),
    ];
  }

  fetchUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authFetchUserSessionStart),
      switchMap(payload => {
        return this.authService.fetchUser(payload.userId).pipe(
          map(response => {
            return actions.authFetchUserSessionSuccess({ user: response });
          }),
          catchError(() => {
            return of(actions.authFetchUserSessionFail());
          }),
        );
      }),
    ),
  );

  fetchUserVerify$ = createEffect(
    this.authService.createGenericRequest({
      actions$: this.actions$,
      makeRequest: () => this.authService.fetchUserVerify(),
      errorMessage: 'Sorry, there has been an error closing your user data',
      startAction: actions.authFetchUserVerifyStart,
      successAction: actions.authFetchUserVerifySuccess,
      failAction: actions.authFetchUserVerifyFail,
    }),
  );

  deleteUserSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authDeleteUserSessionStart),
      switchMap(action => {
        return this.authService.deleteUserSession(action.payload).pipe(
          map(() => {
            // this.snackBarService.addSuccess('You have been logged out');
            return actions.authDeleteUserSessionSuccess({ manualLogout: true });
          }),
          catchError(error => {
            this.loggerService.logEvent({ message: 'error logging out: ', errorResponse: error });
            try {
              // if fail for some reason, let's use brute force and
              // remove the tokens. Doing this the application will
              // route to login page.
              this.storageService.clearLongToken();
              this.storageService.clearShortToken();
              this.storageService.clearUserId();
              this.authService.clearIntercom();
              return of(actions.authDeleteUserSessionSuccess({ manualLogout: true }));
            } catch (e) {
              // if still fails, there is nothing we can do :/
              // let's abort mission
              this.loggerService.logEvent({ message: 'error logging out: ', errorResponse: e as HttpErrorResponse });

              this.snackBarService.setError('Sorry, there has been an error logging you out');
              return of(actions.authDeleteUserSessionFail());
            }
          }),
        );
      }),
    ),
  );

  deleteUserAccount$ = createEffect(
    this.authService.createGenericRequest({
      actions$: this.actions$,
      makeRequest: () => this.authService.deleteUserAccount(),
      errorMessage: 'Sorry, there has been an error closing your account',
      startAction: actions.authDeleteUserAccountStart,
      successAction: actions.authDeleteUserAccountSuccess,
      failAction: actions.authDeleteUserAccountFail,
    }),
  );

  confirmEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authConfirmEmailStart),
      switchMap(action => {
        return this.authService.confirmEmail(action.payload).pipe(
          map(() => {
            return actions.authConfirmEmailSuccess();
          }),
          catchError(() => {
            return of(actions.authConfirmEmailFail());
          }),
        );
      }),
    ),
  );

  registerUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authSendRegisterUserStart),
      switchMap(action => {
        return this.authService.registerUser(action.payload).pipe(
          map(payload => {
            return actions.authSendRegisterUserSuccess({
              payload,
              redditClickId: action.redditClickId,
            });
          }),
          catchError(error => {
            this.loggerService.logEvent({
              message: 'there was an error sending this form - register user',
              errorResponse: error,
            });

            return of(actions.authSendRegisterUserFail());
          }),
        );
      }),
    ),
  );

  // Root effects rather than a listener on the register screen, so the new session is still set up
  // (and the registration counted once) if the user navigates away while the request is in flight.
  registerUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authSendRegisterUserSuccess),
      mergeMap(({ payload, redditClickId }) => {
        const { userId, longTermToken, shortTermToken } = payload;
        this.brazeService.setCustomEvent({
          eventName: BrazeEventEnum.InvestorRegistered,
          userId: userId.toString(),
          // Braze forwards this to Reddit's Conversions API to attribute the registration to an ad click.
          eventProperties: redditClickId ? { [BrazeEventPropertiesEnum.RedditClickId]: redditClickId } : undefined,
        });
        // Tokens before setUser — see setInitialPasswordSuccess$ for rationale.
        this.storageService.setLongToken(longTermToken);
        this.storageService.setShortToken(shortTermToken ?? '');
        this.authService.setUser(`${userId}`);
        return [actions.authFetchUserSessionStart({ userId }), actions.authFetchUserVerifyStart()];
      }),
    ),
  );

  registerUserNavigate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.authSendRegisterUserSuccess),
        switchMap(() => this.actions$.pipe(ofType(actions.authFetchUserVerifySuccess), take(1))),
        tap(() => void this.router.navigate([CREATE_ENTITY_FLOW_ROUTE_PREFIX, 'validate-id'])),
      ),
    { dispatch: false },
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authSendForgotPasswordStart),
      switchMap(action => {
        return this.authService.forgotPassword(action.payload).pipe(
          map(() => {
            return actions.authSendForgotPasswordSuccess();
          }),
          catchError(error => {
            this.loggerService.logEvent({
              message: 'there was an error sending this form - forgot password - error sending email',
              errorResponse: error,
            });
            this.snackBarService.setError('Sorry, there was an error sending your email address');
            return of(actions.authSendForgotPasswordFail());
          }),
        );
      }),
    ),
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authSendResetPasswordStart),
      switchMap(action => {
        return this.authService.resetPassword(action.payload).pipe(
          map(() => {
            return actions.authSendResetPasswordSuccess();
          }),
          catchError(error => {
            this.loggerService.logEvent({ message: 'there was an error resetting password', errorResponse: error });
            this.snackBarService.setError('Sorry, there was an error resetting your password');
            return of(actions.authSendResetPasswordFail());
          }),
        );
      }),
    ),
  );

  setInitialPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authSendSetInitialPasswordStart),
      switchMap(action => {
        return this.authService.setInitialPassword(action.payload).pipe(
          map(payload => {
            return actions.authSendSetInitialPasswordSuccess({ payload });
          }),
          catchError(error => {
            this.loggerService.logEvent({ message: 'there was an error setting password', errorResponse: error });
            this.snackBarService.setError('Sorry, there was an error setting your password');
            return of(actions.authSendSetInitialPasswordFail());
          }),
        );
      }),
    ),
  );

  setInitialPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authSendSetInitialPasswordSuccess),
      mergeMap(response => {
        const { userId, longTermToken, shortTermToken } = response.payload;
        if (userId === undefined || longTermToken == null) {
          this.loggerService.logEvent({
            message: 'authSendSetInitialPasswordSuccess returned without a userId/longTermToken',
          });
          return EMPTY;
        }
        // Tokens MUST be set before setUser (which writes currentUserId). Writing the
        // user id flips reactive queries from disabled→enabled and fires fetches immediately;
        // if the short token isn't in storage yet, those fetches go out without auth → 401 → forced logout.
        this.storageService.setLongToken(longTermToken);
        this.storageService.setShortToken(shortTermToken ?? '');
        this.authService.setUser(`${userId}`);
        return [actions.authFetchUserSessionStart({ userId }), actions.authFetchUserVerifyStart()];
      }),
    ),
  );

  // Route onwards once the user session that was kicked off by setInitialPasswordSuccess$ resolves —
  // the dashboard, unless the screen parked a destination for after authentication (a project
  // borrower's invitation link does). Correlate on userId so an unrelated session fetch (profile,
  // authorized-views, etc.) racing during the route transition can't trigger the nav for the wrong
  // session.
  setInitialPasswordNavigate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.authSendSetInitialPasswordSuccess),
        switchMap(response => {
          const { userId } = response.payload;
          return this.actions$.pipe(
            ofType(actions.authFetchUserSessionSuccess),
            filter(sessionAction => sessionAction.user?.id === userId),
            take(1),
          );
        }),
        tap(() => this.authService.navigateAfterAuth('/')),
      ),
    { dispatch: false },
  );

  hasPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authSendHasPasswordStart),
      switchMap(action => {
        return this.authService.checkHasPassword(action.payload).pipe(
          map(response => {
            return actions.authSendHasPasswordSuccess({ payload: response });
          }),
          catchError(error => {
            this.loggerService.logEvent({
              message: 'there was an error checking if has password',
              errorResponse: error,
            });
            this.snackBarService.setError('Sorry, there was an error checking your token');
            return of(actions.authSendHasPasswordFail());
          }),
        );
      }),
    ),
  );

  changePassword$ = createEffect(() =>
    this.authService.createGenericRequest({
      actions$: this.actions$,
      makeRequest: action => this.authService.changePassword(action.payload),
      errorMessage: 'Sorry, there was an error changing your password',
      startAction: actions.authSendChangePasswordStart,
      successAction: actions.authSendChangePasswordSuccess,
      failAction: actions.authSendChangePasswordFail,
    }),
  );

  registerFirebaseToken$ = createEffect(() =>
    this.authService.createGenericRequest({
      actions$: this.actions$,
      makeRequest: action => this.authService.registerFirebaseToken(action.payload),
      startAction: actions.authRegisterFirebaseTokenStart,
      successAction: actions.authRegisterFirebaseTokenSuccess,
      failAction: actions.authRegisterFirebaseTokenFail,
    }),
  );
}
